import {
  releaseTextBlockWidthsForRow,
  resolveRowLayoutWidth,
} from "../block-width";
import { applyRowColumnContentRebalance } from "../column-content-min";
import {
  evenPercents,
  MAX_COLUMNS,
  normalizePercents,
} from "../column-presets";
import { findRow } from "../find";
import {
  applyRowColumnGutters,
  createColumn,
  rowHasContentGutter,
} from "../factories";
import { sanitizeFillColumnImagesInRow } from "../image-height";
import type { Row } from "../types";
import type { EditorDraft } from "./types";

export function setColumnWidths(
  draft: EditorDraft,
  rowId: string,
  widths: Row['columnWidths'],
): boolean {
  const row = findRow(draft.document, rowId);
  if (!row) {
    return false;
  }
  const normalized = normalizePercents(widths);
  row.columnWidths = normalized;

  const layout = resolveRowLayoutWidth(draft.document, rowId);
  if (layout) {
    releaseTextBlockWidthsForRow(layout.row, layout.innerWidth);
  }

  return true;
}

export function addColumn(
  draft: EditorDraft,
  rowId: string,
  afterColumnId?: string,
): boolean {
  const row = findRow(draft.document, rowId);
  if (!row || row.columns.length >= MAX_COLUMNS) {
    return false;
  }
  const insertAt = afterColumnId
    ? row.columns.findIndex(column => column.id === afterColumnId) + 1
    : row.columns.length;
  const index = Math.max(0, Math.min(insertAt, row.columns.length));
  const newColumn = createColumn();
  row.columns.splice(index, 0, newColumn);
  row.columnWidths = evenPercents(row.columns.length);
  if (row.columns.length > 1 && row.columnGap === 0) {
    row.columnGap = 16;
  }
  if (rowHasContentGutter(row)) {
    applyRowColumnGutters(row);
  }
  applyRowColumnContentRebalance(draft.document, rowId);
  return true;
}

export function removeColumn(
  draft: EditorDraft,
  rowId: string,
  columnId: string,
): boolean {
  const row = findRow(draft.document, rowId);
  if (!row || row.columns.length <= 1) {
    return false;
  }
  const index = row.columns.findIndex(column => column.id === columnId);
  if (index < 0) {
    return false;
  }
  row.columns.splice(index, 1);
  row.columnWidths.splice(index, 1);
  row.columnWidths = normalizePercents(row.columnWidths);
  if (row.columns.length === 1) {
    row.columnGap = 0;
    row.columnWidths = [100];
  } else {
    applyRowColumnContentRebalance(draft.document, rowId);
  }
  if (rowHasContentGutter(row)) {
    applyRowColumnGutters(row);
  }
  sanitizeFillColumnImagesInRow(row);
  draft.selection = { kind: 'row', rowId };
  return true;
}
